/**
 * Tiny self-rolled i18n for the child frontend.
 *
 * Key choices:
 *  - Two locales only: 'zh' (default) and 'en'.
 *  - Flat dotted keys (e.g. 'shop.title'), no namespaces, no plural rules.
 *  - Keys are split across many small files under ./_shell and ./_pages so
 *    multiple feature areas can grow independently. The barrel below merges
 *    them into a single flat lookup table per language. Last-write-wins on
 *    duplicate keys (intentional: lets a later module override a default).
 *  - Source of truth for current language:
 *      1. children.forced_language from API (set by parent admin)  ── highest
 *      2. localStorage 'lang'                                       ── user choice
 *      3. navigator.language starts with 'zh' → 'zh' else 'en'      ── default
 *  - When forced_language is set, setLang() is a no-op (UI must hide/disable
 *    the switcher).
 */
import { useEffect, useSyncExternalStore } from 'react';
import type { AgeTier } from '@xiaopeiban/shared';
import { DEFAULT_AGE_TIER } from '@xiaopeiban/shared';

// ─── Translation tables ──────────────────────────────────────────────────
// Each module exports { zh, en } shape. Add new modules here when a Phase 2
// batch produces a new file. Order is "shell first, then pages alphabetical"
// so debugging missing keys is predictable.
import shell from './_shell/shell';
import auth from './_pages/auth';
import home from './_pages/home';
import shop from './_pages/shop';
import chat from './_pages/chat';
import quiz from './_pages/quiz';
import habits from './_pages/habits';
import wallet from './_pages/wallet';
import zoo from './_pages/zoo';
import building from './_pages/building';
import journal from './_pages/journal';
import imagine from './_pages/imagine';
import timeline from './_pages/timeline';
import me from './_pages/me';
import companion from './_pages/companion';
import scene from './_pages/scene';
import audio from './_pages/audio';
import pokedex from './_pages/pokedex';
import onboarding from './_pages/onboarding';
import rituals from './_pages/rituals';
import games from './_pages/games';
import bond from './_pages/bond';
import park from './_pages/park';
import schedule from './_pages/schedule';
import learn from './_pages/learn';

const MODULES = [
  shell, auth, home, shop, chat, quiz, habits,
  wallet, zoo, building, journal, imagine, timeline, me, companion,
  scene, audio, pokedex, onboarding, rituals, games, bond, park, schedule, learn,
];

function buildTable(lang: 'zh' | 'en'): Record<string, string> {
  const out: Record<string, string> = {};
  for (const mod of MODULES) Object.assign(out, mod[lang] || {});
  return out;
}

export type Lang = 'zh' | 'en';
const TABLES: Record<Lang, Record<string, string>> = {
  zh: buildTable('zh'),
  en: buildTable('en'),
};

const LS_KEY = 'lang';
const FORCED_LS_KEY = 'lang_forced'; // mirror of children.forced_language for offline boots
const EVT = 'xiaopeiban:lang-change';

function detectSystemLang(): Lang {
  if (typeof navigator === 'undefined') return 'zh';
  const raw = (navigator.language || 'zh').toLowerCase();
  return raw.startsWith('zh') ? 'zh' : 'en';
}

function readStoredLang(): Lang | null {
  if (typeof localStorage === 'undefined') return null;
  const v = localStorage.getItem(LS_KEY);
  return v === 'zh' || v === 'en' ? v : null;
}

function readForcedLang(): Lang | null {
  if (typeof localStorage === 'undefined') return null;
  const v = localStorage.getItem(FORCED_LS_KEY);
  return v === 'zh' || v === 'en' ? v : null;
}

let current: Lang = readForcedLang() || readStoredLang() || detectSystemLang();
let forced: Lang | null = readForcedLang();

// v0.12.0 — age tier for the current child. Set once at App boot via
// setI18nAgeTier(getAgeTierFromBirthDate(child.birth_date)). Defaults to
// 'young' so legacy callers see exactly the prior 9yo-tone copy.
let currentTier: AgeTier = DEFAULT_AGE_TIER;
const TIER_EVT = 'xiaopeiban:tier-change';

export function getI18nAgeTier(): AgeTier { return currentTier; }
export function setI18nAgeTier(next: AgeTier): void {
  if (next === currentTier) return;
  currentTier = next;
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(TIER_EVT));
}

export function getLang(): Lang { return current; }
export function isLangForced(): boolean { return forced !== null; }
export function getForcedLang(): Lang | null { return forced; }

export function setLang(next: Lang): void {
  if (forced) return;
  if (next === current) return;
  current = next;
  if (typeof localStorage !== 'undefined') localStorage.setItem(LS_KEY, next);
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(EVT));
}

export function applyForcedLang(next: Lang | null): void {
  forced = next;
  if (typeof localStorage !== 'undefined') {
    if (next) localStorage.setItem(FORCED_LS_KEY, next);
    else localStorage.removeItem(FORCED_LS_KEY);
  }
  const target: Lang = next || readStoredLang() || detectSystemLang();
  if (target !== current) {
    current = target;
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(EVT));
  }
}

function subscribe(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(EVT, cb);
  window.addEventListener(TIER_EVT, cb);
  window.addEventListener('storage', cb);
  return () => {
    window.removeEventListener(EVT, cb);
    window.removeEventListener(TIER_EVT, cb);
    window.removeEventListener('storage', cb);
  };
}

export function useLang(): Lang {
  return useSyncExternalStore(subscribe, getLang, getLang);
}

/**
 * v0.12.0 — Lookup with age-tier fallback chain.
 *
 * For a key `home.dialogue.tap.0` and tier `little`, we try:
 *   1. current language + tiered key   (`home.dialogue.tap.0.little` in zh)
 *   2. current language + bare key     (`home.dialogue.tap.0` in zh)
 *   3. zh fallback + tiered key
 *   4. zh fallback + bare key
 *   5. literal key string (signals a missing entry)
 *
 * Variant keys live alongside their parent in the same module file, e.g.:
 *   'home.dialogue.tap.0':         '你来啦！可以再摸摸我吗？'   ← young (default)
 *   'home.dialogue.tap.0.little':  '主人！抱抱！'              ← 3-5
 *   'home.dialogue.tap.0.older':   '嘿，你回来啦，正想你呢。'   ← 9-11
 *   'home.dialogue.tap.0.tween':   '你可算来了，今天怎么样？'   ← 12-14
 *
 * Authors don't have to add variants for every tier — any missing tier
 * falls back to the default (`young`) string, so partial coverage is OK
 * and incremental tone tuning is safe to ship.
 */
export function t(key: string, vars?: Record<string, string | number>): string {
  const table = TABLES[current];
  const fallback = TABLES.zh;
  const tieredKey = `${key}.${currentTier}`;
  let raw: string | undefined;
  raw = table[tieredKey];
  if (raw === undefined) raw = table[key];
  if (raw === undefined) raw = fallback[tieredKey];
  if (raw === undefined) raw = fallback[key];
  if (raw === undefined) raw = key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, name) => {
    const v = vars[name];
    return v === undefined ? `{${name}}` : String(v);
  });
}

/**
 * Pick the right side of a `中文 / English` style bilingual string. Used for
 * legacy quiz data that hasn't been split into separate fields yet.
 */
export function pickBilingual(combined: string | null | undefined, lang: Lang = current): string {
  if (!combined) return '';
  const sepIdx = (() => {
    const a = combined.indexOf(' / ');
    const b = combined.indexOf('／');
    if (a < 0) return b;
    if (b < 0) return a;
    return Math.min(a, b);
  })();
  if (sepIdx < 0) return combined;
  const zhPart = combined.slice(0, sepIdx).trim();
  const enPart = combined.slice(sepIdx + (combined[sepIdx] === '／' ? 1 : 3)).trim();
  return lang === 'en' ? (enPart || zhPart) : (zhPart || enPart);
}

export function syncDocumentLang(): void {
  if (typeof document === 'undefined') return;
  const update = () => { document.documentElement.lang = current === 'zh' ? 'zh-CN' : 'en'; };
  update();
  if (typeof window !== 'undefined') window.addEventListener(EVT, update);
}

export function useOnLangChange(cb: () => void): void {
  const lang = useLang();
  useEffect(() => { cb(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [lang]);
}
